/**
 * Event status transition with its confirmation dialog. Shared by the page header and any
 * contextual banner (e.g. the public registration card) so they trigger the same flow.
 */

import { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useAuth } from '@/context/authContext';
import { statusActionLabel } from '../utils/statusActions';
import { ReadinessChecklist } from '../components/ReadinessChecklist';
import { useEvent } from './useEvent';
import type { Event, EventStatus } from '@/types/event.types';

const TRANSITION_DESCRIPTION: Record<EventStatus, string> = {
  DRAFT: 'El evento dejará de aceptar inscripciones y no será visible públicamente.',
  OPEN: 'El evento aceptará inscripciones, pagos y registro de asistencia.',
  CLOSED: 'No se aceptarán nuevas inscripciones. Pagos y asistencia siguen habilitados.',
  COMPLETED: 'El evento quedará finalizado. Esta acción no se puede revertir.',
};

export function useEventStatusTransition(event: Event | null, onChanged: (event: Event) => void) {
  const { user } = useAuth();
  const { transitionEventStatus, isSubmitting } = useEvent();
  const [target, setTarget] = useState<EventStatus | null>(null);

  const from = (event?.status ?? 'DRAFT') as EventStatus;

  const handleConfirm = async () => {
    if (!event || !target || !user?.id) return;
    const updated = await transitionEventStatus(event.id, target, user.id);
    setTarget(null);
    if (updated) onChanged(updated);
  };

  const dialog = (
    <AlertDialog open={!!target} onOpenChange={(open) => { if (!open) setTarget(null); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{target && `${statusActionLabel(from, target)}?`}</AlertDialogTitle>
          <AlertDialogDescription>{target && TRANSITION_DESCRIPTION[target]}</AlertDialogDescription>
        </AlertDialogHeader>
        {target === 'OPEN' && event && <ReadinessChecklist event={event} />}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isSubmitting}>Cancelar</AlertDialogCancel>
          <AlertDialogAction onClick={handleConfirm} disabled={isSubmitting}>
            {isSubmitting ? 'Guardando...' : 'Confirmar'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  return { request: setTarget, dialog, isSubmitting };
}
